import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  Bug,
  Check,
  ChevronDown,
  ChevronRight,
  Palette,
  ShieldAlert,
  SlidersHorizontal,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import {
  CHANGE_FILTERS,
  CURRENT_RELEASE,
  HERO_IMAGE,
  RELEASES,
  emptyNotesMessage,
  groupsFor,
  hiddenFeatureCount,
  moreFeaturesFor,
  otherReleases,
  releaseByVersion,
  spotlightFor,
  statPills,
  tabCounts,
  type ChangeFilter,
  type GroupTone,
  type NoteType,
  type SpotlightCard,
} from '../../data/whats-new';
import { EmptyIllustration, HighlightArt, ReleaseIllustration } from './ReleaseIllustration';
import './whats-new.css';

const PREVIEW_MS = 680;
const PREVIOUS_PREVIEW = 3;

const TONE_ICON: Record<GroupTone, LucideIcon> = {
  feature: Sparkles,
  enhance: Palette,
  improve: SlidersHorizontal,
  fix: Bug,
  crash: ShieldAlert,
};

const ITEM_ICON: Record<NoteType, LucideIcon> = {
  new_feature: Sparkles,
  ui_enhancement: Palette,
  improvement: SlidersHorizontal,
  bug_fix: Bug,
  crash: ShieldAlert,
};

export interface WhatsNewViewProps {
  /** Leave unset to preview skeletons. Pass a real flag once the release API is wired. */
  loading?: boolean;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function usePreviewLoading(enabled: boolean): boolean {
  const [loading, setLoading] = useState(() => enabled && !prefersReducedMotion());

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    if (prefersReducedMotion()) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const id = window.setTimeout(() => setLoading(false), PREVIEW_MS);
    return () => window.clearTimeout(id);
  }, [enabled]);

  return loading;
}

function scrollToId(id: string): void {
  const node = document.getElementById(id);
  if (!node || typeof node.scrollIntoView !== 'function') return;
  try {
    node.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  } catch {
    /* Some test environments do not implement scrolling. */
  }
}

function Skel({ className = '' }: { className?: string }) {
  return <span className={`wn-skel ${className}`} />;
}

function NoteImage({ src, alt }: { src: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <figure className="wn-item__figure">
      <img
        className="wn-item__shot"
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    </figure>
  );
}

function NoteThumb({ src }: { src: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      className="wn-item__thumb"
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

function SpotlightVisual({ card }: { card: SpotlightCard }) {
  const [failed, setFailed] = useState(false);
  if (!card.image || failed) return <HighlightArt tone={card.tone} />;
  return (
    <img
      className="wn-spot__shot"
      src={card.image}
      alt=""
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

function HeroArt({ showCards }: { showCards: boolean }) {
  const [failed, setFailed] = useState(false);
  if (!showCards || !HERO_IMAGE || failed) {
    return <ReleaseIllustration cards={showCards} />;
  }
  return (
    <div className="wn-illus wn-illus--photo" data-testid="hero-image">
      <img
        src={HERO_IMAGE}
        alt="Loop playlist"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

function HeaderSkeleton() {
  return (
    <div className="wn-toolbar" data-testid="skeleton-header" aria-hidden="true">
      <div className="wn-toolbar__tools">
        <Skel className="wn-skel--field wn-skel--short" />
      </div>
    </div>
  );
}

function HeroSkeleton() {
  return (
    <div className="wn-hero" data-testid="skeleton-hero" aria-hidden="true">
      <div>
        <Skel className="wn-skel--badge" />
        <Skel className="wn-skel--version" />
        <Skel className="wn-skel--line" />
        <Skel className="wn-skel--line wn-skel--mid" />
        <div className="wn-stats">
          {[0, 1, 2, 3].map((key) => (
            <Skel key={key} className="wn-skel--pill" />
          ))}
        </div>
        <Skel className="wn-skel--btn" />
      </div>
      <Skel className="wn-skel--art" />
    </div>
  );
}

function HighlightsSkeleton() {
  return (
    <div className="wn-skel-stack" data-testid="skeleton-highlights" aria-hidden="true">
      <Skel className="wn-skel--line" />
      <div className="wn-highlights">
        {[0, 1, 2].map((key) => (
          <Skel key={key} className="wn-skel--card" />
        ))}
      </div>
    </div>
  );
}

function MoreSkeleton() {
  return (
    <div className="wn-card wn-skel-stack" data-testid="skeleton-more" aria-hidden="true">
      <Skel className="wn-skel--line" />
      <div className="wn-more__list">
        {[0, 1, 2, 3].map((key) => (
          <Skel key={key} className="wn-skel--chip" />
        ))}
      </div>
    </div>
  );
}

function BoardSkeleton() {
  return (
    <div className="wn-board">
      <div className="wn-card wn-skel-stack" data-testid="skeleton-changes" aria-hidden="true">
        <Skel className="wn-skel--line" />
        <Skel className="wn-skel--row" />
        <Skel className="wn-skel--row" />
        <Skel className="wn-skel--row" />
      </div>
      <div className="wn-card wn-skel-stack" data-testid="skeleton-previous" aria-hidden="true">
        <Skel className="wn-skel--line" />
        <Skel className="wn-skel--row" />
        <Skel className="wn-skel--row" />
        <Skel className="wn-skel--row" />
      </div>
    </div>
  );
}

const WhatsNewView: React.FC<WhatsNewViewProps> = ({ loading: loadingProp }) => {
  const preview = usePreviewLoading(loadingProp === undefined);
  const loading = loadingProp ?? preview;
  const [version, setVersion] = useState('all');
  const [filter, setFilter] = useState<ChangeFilter>('all');
  const [showAllPrevious, setShowAllPrevious] = useState(false);
  const focused = version === 'all' ? CURRENT_RELEASE : releaseByVersion(version);
  const spotlight = useMemo(() => spotlightFor(focused), [focused]);
  const more = useMemo(
    () => moreFeaturesFor(focused, spotlight.map((card) => card.id)),
    [focused, spotlight]
  );
  const groups = useMemo(() => groupsFor(focused, filter, ''), [focused, filter]);
  const others = useMemo(() => otherReleases(focused.version, ''), [focused]);
  const pills = useMemo(() => statPills(focused), [focused]);
  const counts = useMemo(() => tabCounts(focused, ''), [focused]);
  const hiddenFeatures = hiddenFeatureCount(
    focused,
    spotlight.map((card) => card.id).concat(more.map((item) => item.id))
  );
  const shownOthers = showAllPrevious ? others : others.slice(0, PREVIOUS_PREVIEW);
  const hiddenPrevious = Math.max(0, others.length - shownOthers.length);
  const firstGroupId = groups[0]?.id ?? '';
  const firstItemId = groups[0]?.items[0]?.id ?? '';
  const [openGroups, setOpenGroups] = useState<string[]>(() => (firstGroupId ? [firstGroupId] : []));
  const [openItems, setOpenItems] = useState<Record<string, boolean>>(() =>
    firstItemId ? { [firstItemId]: true } : {}
  );

  useEffect(() => {
    setOpenGroups(firstGroupId ? [firstGroupId] : []);
    setOpenItems(firstItemId ? { [firstItemId]: true } : {});
  }, [focused.version, filter, firstGroupId, firstItemId]);

  const onExplore = () => {
    setFilter('all');
    scrollToId('wn-changes');
  };

  const onViewAll = () => {
    setShowAllPrevious(true);
    scrollToId('wn-previous');
  };

  const onSeeFeatures = () => {
    setFilter('features');
    scrollToId('wn-changes');
  };

  const toggleGroup = (id: string) => {
    setOpenGroups((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  };

  const toggleItem = (id: string) => {
    setOpenItems((current) => ({ ...current, [id]: !current[id] }));
  };

  if (loading) {
    return (
      <div className="wn" data-testid="whats-new" aria-busy="true">
        <div className="visually-hidden" role="status">
          Loading what&apos;s new
        </div>
        <HeaderSkeleton />
        <HeroSkeleton />
        <HighlightsSkeleton />
        <MoreSkeleton />
        <BoardSkeleton />
      </div>
    );
  }

  return (
    <div className="wn" data-testid="whats-new" aria-busy="false">
      <header className="wn-toolbar">
        <div className="wn-toolbar__tools">
          <label className="wn-version-wrap">
            <span className="visually-hidden">Version</span>
            <select className="wn-version" value={version} onChange={(event) => setVersion(event.target.value)}>
              <option value="all">All versions</option>
              {RELEASES.map((release) => (
                <option key={release.version} value={release.version}>
                  {release.isCurrent ? `v${release.version} · Latest` : `v${release.version}`}
                </option>
              ))}
            </select>
            <ChevronDown size={16} className="wn-version-wrap__icon" aria-hidden="true" />
          </label>
        </div>
      </header>

      <section className="wn-hero" aria-labelledby="wn-hero-title">
        <div className="wn-hero__copy">
          <p className={focused.isCurrent ? 'wn-kicker' : 'wn-kicker wn-kicker--past'}>
            {focused.isCurrent ? 'Latest release' : 'Earlier release'}
          </p>
          <div className="wn-version-row">
            <p className="wn-version-num">v{focused.version}</p>
            <p className="wn-updated">
              {focused.isCurrent ? `Latest update ${focused.dateLabel}` : focused.dateLabel}
            </p>
          </div>
          <h2 id="wn-hero-title" className="wn-headline">
            {focused.headline}
          </h2>
          <p className="wn-summary">{focused.summary}</p>
          <div className="wn-stats">
            {pills.map((pill) => {
              const Icon = TONE_ICON[pill.tone];
              return (
                <span key={pill.id} className={`wn-stat wn-stat--${pill.tone}`}>
                  <span className="wn-stat__icon" aria-hidden="true">
                    <Icon size={13} />
                  </span>
                  {pill.label}
                </span>
              );
            })}
          </div>
          <button type="button" className="wn-cta" onClick={onExplore}>
            Explore all changes
            <ArrowRight size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="wn-hero__art">
          <HeroArt showCards={focused.isCurrent} />
        </div>
      </section>

      {spotlight.length > 0 ? (
        <section className="wn-block" aria-labelledby="wn-highlights-title">
          <div className="wn-section__head">
            <h2 id="wn-highlights-title">Highlights in this release</h2>
            <button type="button" className="wn-link" onClick={onViewAll}>
              View all updates
              <ArrowRight size={16} aria-hidden="true" />
            </button>
          </div>
          <div className="wn-highlights">
            {spotlight.map((card) => (
              <article key={card.id} className={`wn-spot wn-spot--${card.tone}`}>
                <div className="wn-spot__copy">
                  <p className="wn-spot__kicker">{card.kicker}</p>
                  <h3>{card.title}</h3>
                  <p className="wn-spot__blurb">{card.blurb}</p>
                </div>
                <SpotlightVisual card={card} />
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {more.length > 0 ? (
        <section className="wn-card wn-more" aria-labelledby="wn-more-title">
          <div className="wn-section__head">
            <h2 id="wn-more-title">More new features</h2>
            {hiddenFeatures > 0 ? (
              <button type="button" className="wn-link" onClick={onSeeFeatures}>
                See all features
              </button>
            ) : null}
          </div>
          <ul className="wn-more__list">
            {more.map((item) => (
              <li key={item.id}>
                <span className="wn-check" aria-hidden="true">
                  <Check size={12} strokeWidth={3} />
                </span>
                {item.title}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="wn-board">
        <section id="wn-changes" className="wn-card wn-changes" aria-label="Release changes">
          <div className="wn-tabs" role="tablist" aria-label="Change types">
            {CHANGE_FILTERS.map((tab) => {
              const selected = filter === tab.id;
              const label = tab.id === 'all' ? tab.label : `${tab.label} (${counts[tab.id]})`;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={selected ? 'wn-tab is-active' : 'wn-tab'}
                  onClick={() => setFilter(tab.id)}
                >
                  {label}
                </button>
              );
            })}
          </div>
          {groups.length === 0 ? (
            <div className="wn-empty" role="status">
              <EmptyIllustration />
              <p>{emptyNotesMessage('', filter, focused.version)}</p>
              <div className="wn-empty__actions">
                {filter !== 'all' ? (
                  <button type="button" className="wn-link" onClick={() => setFilter('all')}>
                    Show all changes
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            groups.map((group) => {
              const open = openGroups.includes(group.id);
              const Icon = TONE_ICON[group.tone];
              return (
                <div key={group.id} className={`wn-group wn-group--${group.tone}`}>
                  <button
                    type="button"
                    className="wn-group__toggle"
                    aria-expanded={open}
                    onClick={() => toggleGroup(group.id)}
                  >
                    <span className="wn-group__icon" aria-hidden="true">
                      <Icon size={16} />
                    </span>
                    <span className="wn-group__title">{group.title}</span>
                    <span className="wn-group__meta">{group.meta}</span>
                    <ChevronDown size={16} className="wn-group__chevron" aria-hidden="true" />
                  </button>
                  {open ? (
                    <div className="wn-group__body">
                      {group.items.map((item) => {
                        const itemOpen = Boolean(openItems[item.id]);
                        const ItemIcon = ITEM_ICON[item.type];
                        return (
                          <div key={item.id} className={itemOpen ? 'wn-item is-open' : 'wn-item'}>
                            <button
                              type="button"
                              className="wn-item__btn"
                              aria-expanded={itemOpen}
                              onClick={() => toggleItem(item.id)}
                            >
                              <span className={`wn-item__icon wn-item__icon--${item.type}`} aria-hidden="true">
                                <ItemIcon size={14} />
                              </span>
                              <span className="wn-item__title">{item.title}</span>
                              {!itemOpen && item.image ? <NoteThumb src={item.image} /> : null}
                              <ChevronRight size={16} className="wn-item__chevron" aria-hidden="true" />
                            </button>
                            {itemOpen ? (
                              <div className="wn-item__panel">
                                <p className="wn-item__detail">{item.detail}</p>
                                {item.image ? <NoteImage src={item.image} alt={item.title} /> : null}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </section>

        <section id="wn-previous" className="wn-card" aria-labelledby="wn-previous-title">
          <h2 id="wn-previous-title">Previous versions</h2>
          {shownOthers.length === 0 ? (
            <p className="wn-prev__none">No other versions match.</p>
          ) : (
            <ul className="wn-prev__list">
              {shownOthers.map((release) => {
                const active = version === release.version;
                return (
                  <li key={release.version} className="wn-prev__row">
                    <span className="wn-prev__rail" aria-hidden="true">
                      <span className="wn-prev__dot" data-testid="version-dot" />
                    </span>
                    <button
                      type="button"
                      className={active ? 'wn-prev__item is-active' : 'wn-prev__item'}
                      aria-current={active ? 'true' : undefined}
                      onClick={() => setVersion(release.version)}
                    >
                      <span className="wn-prev__top">
                        <strong className="wn-prev__version">v{release.version}</strong>
                        <span className="wn-prev__count">{release.updateLabel}</span>
                        <span className="wn-prev__date">{release.dateLabel}</span>
                      </span>
                      <span className="wn-prev__name">{release.title}</span>
                      <ChevronRight size={16} className="wn-prev__chev" aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {hiddenPrevious > 0 ? (
            <button type="button" className="wn-card__more" onClick={() => setShowAllPrevious(true)}>
              Show {hiddenPrevious} more versions
            </button>
          ) : null}
        </section>
      </div>
    </div>
  );
};

export default WhatsNewView;
